import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateEventSchema } from '../../utils/seo';

const FestivalsPage = () => {
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  // SEO設定
  useSEO({
    title: '京都山科 年間祭礼行事 | 大石神社',
    description: '大石神社の年間祭礼行事のご案内。歳旦祭、節分祭、春季例大祭、大石桜観賞、端午の節句祭、七夕祭、秋季例大祭、七五三祝祭、義士祭など、四季折々の祭事を執り行っております。12月14日の義士祭は最大の行事です。',
    keywords: '祭礼行事,義士祭,大石桜,節分祭,七五三,例大祭,年中行事,祭り,イベント,大石神社,京都,山科',
    canonical: '/festivals',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateEventSchema('義士祭', '討ち入りの日を記念した最も重要な祭典。義士装束に身を包んだ行列が神社を目指します', '2024-12-14', '大石神社'),
        generateEventSchema('大石桜観賞', '境内の見事なしだれ桜「大石桜」が満開を迎えます', '2024-03-25', '大石神社')
      ]
    }
  });

  const months = [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ];

  const festivals = [
    {
      month: 1,
      date: '1月1日',
      title: '歳旦祭',
      description: '新年を祝い、一年の平安と繁栄を祈願する祭典です。多くの初詣参拝者で賑わいます。',
      image: 'https://public.readdy.ai/ai/img_res/72efc046be925a805adca18659ae54f1.jpg'
    },
    {
      month: 2,
      date: '2月3日',
      title: '節分祭',
      description: '豆まきを行い、邪気を払い福を招きます。境内では福豆の授与も行われます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20shrine%20setsubun%20festival%20bean%20throwing%20ceremony%20people%20celebrating%20traditional%20event%20winter%20scene%20festive%20atmosphere%20cultural%20tradition&width=800&height=500&seq=fest002&orientation=landscape'
    },
    {
      month: 3,
      date: '3月下旬〜4月上旬',
      title: '大石桜観賞',
      description: '境内の見事なしだれ桜「大石桜」が満開を迎えます。夜間はライトアップも実施され、幻想的な雰囲気に包まれます。',
      image: 'https://readdy.ai/api/search-image?query=Magnificent%20weeping%20cherry%20blossom%20tree%20in%20full%20bloom%20at%20Japanese%20shrine%20with%20visitors%20admiring%20flowers%20spring%20evening%20illumination%20magical%20pink%20petals%20traditional%20architecture%20peaceful%20atmosphere&width=800&height=500&seq=fest003&orientation=landscape'
    },
    {
      month: 4,
      date: '4月14日',
      title: '春季例大祭',
      description: '春の訪れを祝う重要な祭典。神楽の奉納や雅楽の演奏が行われます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20shrine%20spring%20festival%20ceremony%20with%20traditional%20music%20performance%20kagura%20dance%20elegant%20atmosphere%20cherry%20blossoms%20cultural%20celebration&width=800&height=500&seq=fest004&orientation=landscape'
    },
    {
      month: 5,
      date: '5月5日',
      title: '端午の節句祭',
      description: '子供たちの健やかな成長を祈願する祭典。鯉のぼりが境内を彩ります。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20shrine%20childrens%20day%20festival%20with%20colorful%20carp%20streamers%20koinobori%20flying%20in%20wind%20families%20celebrating%20traditional%20event%20spring%20atmosphere&width=800&height=500&seq=fest005&orientation=landscape'
    },
    {
      month: 7,
      date: '7月7日',
      title: '七夕祭',
      description: '笹飾りに願い事を書いた短冊を結び、星に祈りを捧げます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20shrine%20tanabata%20festival%20with%20bamboo%20decorations%20colorful%20paper%20wishes%20tanzaku%20summer%20evening%20peaceful%20atmosphere%20traditional%20celebration&width=800&height=500&seq=fest006&orientation=landscape'
    },
    {
      month: 10,
      date: '10月14日',
      title: '秋季例大祭',
      description: '実りの秋に感謝を捧げる祭典。神輿渡御や奉納演舞が行われます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20shrine%20autumn%20festival%20with%20portable%20shrine%20mikoshi%20procession%20traditional%20costumes%20fall%20foliage%20vibrant%20atmosphere%20cultural%20celebration&width=800&height=500&seq=fest007&orientation=landscape'
    },
    {
      month: 11,
      date: '11月15日',
      title: '七五三祝祭',
      description: '子供たちの成長を祝い、健康と幸福を祈願します。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20shrine%20shichi-go-san%20celebration%20with%20children%20in%20traditional%20kimono%20families%20praying%20autumn%20atmosphere%20joyful%20cultural%20tradition&width=800&height=500&seq=fest008&orientation=landscape'
    },
    {
      month: 12,
      date: '12月14日',
      title: '義士祭',
      description: '討ち入りの日を記念した最も重要な祭典。義士装束に身を包んだ行列が神社を目指し、多くの参拝者で賑わいます。',
      image: 'https://readdy.ai/api/search-image?query=Grand%20Japanese%20festival%20procession%20with%20people%20in%20samurai%20warrior%20costumes%20marching%20through%20snowy%20streets%20towards%20shrine%20winter%20night%20illuminated%20by%20lanterns%20historical%20reenactment%20dramatic%20atmosphere&width=800&height=500&seq=fest009&orientation=landscape'
    }
  ];

  const filteredFestivals = selectedMonth !== null 
    ? festivals.filter(f => f.month === selectedMonth + 1)
    : festivals;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-[#D4AF37] font-serif text-6xl font-bold mb-6 tracking-wide">祭礼行事</h1>
            <div className="w-24 h-[1px] bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-white text-lg leading-relaxed">
              大石神社では、年間を通じて様々な祭礼行事を執り行っております。
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <button
                onClick={() => setSelectedMonth(null)}
                className={`px-6 py-2 rounded-full text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                  selectedMonth === null
                    ? 'bg-[#D4AF37] text-black'
                    : 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black'
                }`}
              >
                すべて
              </button>
              {months.map((month, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedMonth(index)}
                  className={`px-6 py-2 rounded-full text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                    selectedMonth === index
                      ? 'bg-[#D4AF37] text-black'
                      : 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black'
                  }`}
                >
                  {month}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="space-y-8 mb-24">
            {filteredFestivals.map((festival, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#1A1A1A] border-l-4 border-[#D4AF37] rounded-lg overflow-hidden hover:shadow-[0_0_30px_rgba(212,175,55,0.2)] transition-all group"
              >
                <div className="grid grid-cols-[40%_60%]">
                  <div className="h-80 overflow-hidden">
                    <img 
                      src={festival.image} 
                      alt={festival.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <p className="text-white text-2xl font-bold mb-3">{festival.date}</p>
                    <h3 className="text-[#D4AF37] text-3xl font-serif font-bold mb-4 tracking-wide">{festival.title}</h3>
                    <p className="text-gray-400 leading-loose">{festival.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#1A1A1A] to-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg p-16 relative overflow-hidden"
          >
            <div 
              className="absolute inset-0 opacity-20 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Spectacular%20Japanese%20shrine%20festival%20procession%20at%20night%20in%20winter%20with%20hundreds%20of%20people%20in%20historical%20samurai%20costumes%20marching%20through%20illuminated%20snowy%20streets%20grand%20celebration%20dramatic%20lighting%20atmospheric%20scene&width=1200&height=600&seq=gishi002&orientation=landscape')`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60"></div>
            
            <div className="relative z-10">
              <div className="grid grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-block px-4 py-1 bg-[#D4AF37]/20 border border-[#D4AF37] rounded-full mb-6">
                    <span className="text-[#D4AF37] text-sm tracking-wider">特別行事</span>
                  </div>
                  <h2 className="text-[#D4AF37] font-serif text-5xl font-bold mb-6 tracking-wide">義士祭</h2>
                  <p className="text-white leading-loose mb-6">
                    毎年12月14日に開催される義士祭は、大石神社最大の行事です。討ち入りの日を記念し、義士装束に身を包んだ地元の人々が行列を組み、神社を目指します。
                  </p>
                  <div className="space-y-3 text-gray-300">
                    <div className="flex items-start gap-3">
                      <i className="ri-calendar-line text-[#D4AF37] mt-1"></i>
                      <div>
                        <p className="font-semibold">開催日時</p>
                        <p className="text-sm">毎年12月14日 10:00〜15:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-map-pin-line text-[#D4AF37] mt-1"></i>
                      <div>
                        <p className="font-semibold">行列ルート</p>
                        <p className="text-sm">山科駅周辺から大石神社まで</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-group-line text-[#D4AF37] mt-1"></i>
                      <div>
                        <p className="font-semibold">参加者</p>
                        <p className="text-sm">約300名の義士装束行列</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Close-up%20of%20person%20in%20traditional%20samurai%20warrior%20costume%20at%20Japanese%20festival%20wearing%20authentic%20armor%20and%20helmet%20winter%20scene%20historical%20reenactment%20detailed%20traditional%20attire&width=400&height=500&seq=gishi003&orientation=portrait" 
                    alt="義士装束"
                    className="w-full h-64 object-cover rounded-lg border border-[#D4AF37]"
                  />
                  <img 
                    src="https://readdy.ai/api/search-image?query=Japanese%20shrine%20festival%20procession%20with%20traditional%20banners%20and%20flags%20people%20marching%20in%20historical%20costumes%20winter%20celebration%20cultural%20heritage%20event&width=400&height=500&seq=gishi004&orientation=portrait" 
                    alt="行列"
                    className="w-full h-64 object-cover rounded-lg border border-[#D4AF37] mt-8"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FestivalsPage;
